import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api, { formatApiError, setStoredToken, getStoredToken } from "./api";
import { biometricAvailable, getBiometricToken, saveBiometricToken, clearBiometricToken, isBiometricOptedIn } from "./biometric";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const r = await api.get("/auth/me");
      // V15.x: sessão deslizante — persiste o token renovado devolvido pelo /auth/me.
      if (r.data?.session_token) setStoredToken(r.data.session_token);
      setUser(r.data);
      return r.data;
    } catch (e) {
      const status = e?.response?.status;
      // V8.5.5: só desloga em erros 401 (token inválido). Outros erros (rede, 5xx)
      // mantém o user logado — evita flicker em redes instáveis.
      if (status === 401 || status === 403) {
        setUser(null);
      }
      console.error("[auth] refresh failed:", status || e?.message);
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      // V17.9.8 — Biometric login: se não há token na sessão mas o usuário optou
      // por biometria neste device, tenta desbloquear com FaceID/TouchID/digital
      // ANTES do refresh() (que falharia sem token e mostraria a tela de login).
      try {
        if (!getStoredToken() && isBiometricOptedIn()) {
          const { available } = await biometricAvailable();
          if (available) {
            const t = await getBiometricToken();
            if (t) setStoredToken(t);
          }
        }
      } catch (e) { console.warn("[auth] biometric boot skipped:", e?.message); }
      refresh().finally(() => setLoading(false));
    })();
  }, []);

  // Ao voltar pro app (Capacitor resume) ou a aba ganhar foco → recarrega o user.
  // Garante que aprovações do admin (KYC, wallet) apareçam sem precisar relogar.
  useEffect(() => {
    const onResume = () => { refresh(); };
    window.addEventListener("aj:app-resume", onResume);
    return () => window.removeEventListener("aj:app-resume", onResume);
  }, []);

  // V8.5.5 — Auto-refresh do user em (a) foco da tab, (b) visibilitychange, (c) intervalo 60s.
  // Garante que mudanças de estado feitas pelo admin (KYC aprovado, wallet creditada, etc.)
  // aparecem sem o user precisar dar F5.
  useEffect(() => {
    if (!user?.id) return undefined;
    let lastFetch = Date.now();
    const POLL_MS = 60_000;  // 60s
    const onFocus = () => {
      // throttle: só refaz se passou >5s desde último fetch
      if (Date.now() - lastFetch > 5_000) {
        lastFetch = Date.now();
        refresh();
      }
    };
    const onVis = () => {
      if (document.visibilityState === "visible") onFocus();
    };
    const interval = setInterval(() => {
      lastFetch = Date.now();
      refresh();
    }, POLL_MS);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user?.id]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) {
        setStoredToken(data.token);
        // V17.9.8 — se o usuário já optou por biometria neste device (Perfil → biometria),
        // atualiza o token guardado no Keychain/Keystore automaticamente.
        if (isBiometricOptedIn()) {
          try { await saveBiometricToken(data.token); } catch (e) { console.warn("[auth] biometric refresh save failed:", e?.message); }
        }
      }
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.token) setStoredToken(data.token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    // V17.9.9 — LIMPA ESTADO LOCAL PRIMEIRO. Antes o `await` da chamada /auth/logout
    // e do clearBiometricToken() no Android WebView travava o setUser(null) até a
    // resposta remota chegar — o usuário via o botão "Sair" sem feedback e só saía
    // ao refresh manual da tela.
    setStoredToken(null);
    setUser(null);
    try { await api.post("/auth/logout"); } catch (e) { console.error("[auth] logout API failed (ignored):", e); }
    try { await clearBiometricToken(); } catch (e) { console.warn("[auth] biometric clear on logout failed:", e?.message); }
  };

  // V17.9.8 — Ativa/desativa login biométrico (chamado pela tela de Perfil).
  // Ao ATIVAR: salva o token atual protegido por biometria (requer prompt biométrico
  // no primeiro uso após o app ser reiniciado).
  const enableBiometric = async () => {
    const t = getStoredToken();
    if (!t) return { ok: false, error: "Faça login antes de ativar a biometria." };
    const { available, reason } = await biometricAvailable();
    if (!available) return { ok: false, error: reason === "not_native" ? "Disponível apenas no app instalado (iOS/Android)." : "Este device não tem biometria configurada." };
    const ok = await saveBiometricToken(t);
    return ok ? { ok: true } : { ok: false, error: "Não foi possível salvar a credencial biométrica." };
  };
  const disableBiometric = async () => { await clearBiometricToken(); return { ok: true }; };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh, enableBiometric, disableBiometric }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
